/**
 * `/admin/auth` - the admin session (spec 03's routes, consumed by spec 12).
 *
 * `POST /admin/auth/login` refuses a member's credentials with the same generic
 * 401 it gives a wrong password (`auth_service.authenticate_admin`), which is
 * FR-8.1b: a member must not be able to tell "no such admin" from "wrong
 * password", and an admin's existence must not be probeable from this app.
 *
 * The client does not send `device_platform`: the route sets `web` itself, and
 * the column's CHECK would refuse anything else.
 */

import { request } from "./client";
import { session, type AdminIdentity } from "../session";

export type AdminSession = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  admin: AdminIdentity;
};

/** Log in and adopt the session. Throws `ApiError` (401) for any bad credential. */
export async function login(identifier: string, password: string): Promise<AdminIdentity> {
  const body = await request<AdminSession>("/admin/auth/login", {
    method: "POST",
    body: { identifier, password },
    anonymous: true,
  });
  session.start(body.access_token, body.refresh_token, body.admin);
  return body.admin;
}

/**
 * End the session. Idempotent on the server, and deliberately tolerant here:
 * the admin asked to be signed out, so a network failure must still sign them
 * out locally rather than trapping them in a session they cannot leave.
 */
export async function logout(): Promise<void> {
  const refreshToken = session.getRefreshToken();
  try {
    if (refreshToken) {
      await request("/admin/auth/logout", {
        method: "POST",
        body: { refresh_token: refreshToken },
      });
    }
  } catch {
    // Ignored on purpose: see above.
  } finally {
    session.clear();
  }
}
